import { isAutoVehicleComplete } from './vehicleCompleteness.js';
import { isMarineVehicleComplete } from './marineService.js';
import { isMarineVehicle } from './assetType.js';
import { normalizeStoredVehicle } from './storedVehicle.js';

/**
 * Is this vehicle chosen completely enough to read the service catalog for?
 *
 * The services step refetches on every change to the vehicle prop, and the
 * cascade emits a NEW vehicle after each dropdown — so without this the fetch
 * fired on year, then make, then model, then trim, before there was anything to
 * filter on. Those requests went out with an empty `vehicleSize` and no
 * `bodyStyle`.
 *
 * **Read the catalog only once the vehicle is fully selected**, for auto and
 * marine alike. A half-chosen vehicle does not merely fail — it can SUCCEED and
 * return the wrong catalog: `catalogFilterParams` falls back to `vehicleSize`
 * when there is no body style, so a vehicle with a size but no trim would fetch
 * a plausible-looking list filtered on the wrong axis.
 *
 * Reuses the SAME predicates that gate the Next button rather than inventing a
 * looser rule, so the catalog can never be read for a vehicle the wizard itself
 * considers unfinished. Two rules for one question is how they drift.
 *
 * The two branches genuinely differ: a boat has no trim, body style or
 * `id_car_trim` — the marine catalog is keyed on `assetType` + `vehicle_size`
 * 'SF' — so the automotive gate would block it forever.
 *
 * Imports only zero-import modules, so this stays harness-testable
 * (`scripts/test/catalogReadiness.test.mjs`). The `.js` extensions are required:
 * Node's ESM resolver does not guess them and webpack does not mind.
 */
export function isVehicleReadyForCatalog(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') return false;
  return isMarineVehicle(vehicle)
    ? isMarineVehicleComplete(vehicle)
    : isAutoVehicleComplete(vehicle);
}

/**
 * EDIT MODE — the vehicle is a row read back from `GET /vehicles/:vin`, not a
 * wizard object. The wizard gate above requires `body_style` and `id_car_trim`,
 * which are catalog-cascade fields a stored row need not carry (a walk-in
 * vehicle is stored as year/make/model/trim/body/size). Judged by the wizard
 * rule, the edit screen never read the catalog and never opened the job
 * picker, so its services step stayed empty (request 8634, 2026-09-11; the
 * gate landed 2026-09-02).
 *
 * A stored row is complete by definition — it is the row the catalog will be
 * read against — so the test is "persisted, and identifies a vehicle". Scoped
 * to edit mode deliberately: `emitAutoVehicleState` re-emits a saved vehicle's
 * `id` on every later dropdown change, so in WIZARD mode an id proves nothing
 * and the full rule above must stay in force.
 */
export function isStoredVehicleReadyForCatalog(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') return false;
  // The row's identity columns are PascalCase (Vehicle_ID, Year, Make, Model,
  // VIN); read either casing — see util/storedVehicle.js.
  const v = normalizeStoredVehicle(vehicle);
  const id = Number(v.id);
  if (!Number.isFinite(id) || id <= 0) return false;
  if (isMarineVehicle(v)) return isMarineVehicleComplete(v);
  return Boolean(v.year && v.make && v.model);
}

const catalogReadiness = { isVehicleReadyForCatalog, isStoredVehicleReadyForCatalog };
export default catalogReadiness;
